import * as Y from 'yjs';
import { useEffect, useMemo } from 'react';
import { Skeleton } from '@nula/core-ui';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';
import { canvasRoots, nestedBinding, type CanvasBinding, TOP_LEVEL_BINDING } from '@/features/canvas/engine/wiring/canvas-roots';
import { CanvasBindingContext } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { CanvasViewer } from './canvas-viewer';
import { CanvasEditor } from './shell/canvas-editor';
import styles from './canvas-viewer.preview.module.css';

interface CanvasViewerPreviewProps {
  entityId: string;
  onStats?: (stats: { nodeCount: number }) => void;
  /**
   * Where the canvas's Yjs roots live in the entity's doc. Defaults to
   * top-level (a standalone canvas entity). A nested binding lets a host
   * feature preview a canvas embedded under a parent map in its own doc —
   * reachable through the `lib/canvas` injection seam with no cross-feature
   * import. Provided to CanvasViewer via CanvasBindingContext.
   */
  binding?: CanvasBinding;
  /**
   * WHICH Yjs room to resolve (default `'canvas'`). A nested canvas in a
   * non-canvas entity's doc (e.g. a document-surfaces diagram) must resolve
   * the HOST entity's room — pass `'document'`. The default keeps every
   * standalone-canvas caller byte-identical.
   */
  entityKindHint?: YjsRoomKind;
  /**
   * Cross-feature-safe binding request. When true, this renderer constructs the
   * standard nested-canvas binding (`nestedBinding(nestedKey)`) internally, so a
   * host feature (e.g. `features/document-surfaces`) never imports
   * `nestedBinding`. Wins over an explicit `binding` if both are set.
   */
  nested?: boolean;
  /**
   * The parent map key `nested` nests under (default `'canvas'`, which keeps
   * every existing caller byte-identical). A host that can embed more than one
   * canvas in the same doc — e.g. several canvas surfaces on one document —
   * must pass a key unique per embed, or every embed resolves the same roots.
   */
  nestedKey?: string;
  /**
   * Whether the embedded canvas is read-only. Defaults to `true` (preview =
   * view-only, the standalone-preview + diff/snapshot callers). A host feature
   * that has resolved edit permission passes `readOnly={false}` to get the
   * INTERACTIVE `CanvasEditor` mount instead — writing through the same nested
   * binding + host room. Permission is the host's responsibility (mirrors how a
   * standalone canvas gates `CanvasEditor` via `usePermission`).
   */
  readOnly?: boolean;
}

/** Default parent map key for a canvas nested in a host entity's Yjs doc. */
const DEFAULT_NESTED_CANVAS_PARENT_KEY = 'canvas';

/**
 * Read-only canvas preview — mirrors DocumentViewer for the canvas case.
 *
 * Subscribes to the entity's Yjs room (`entityKindHint`, default 'canvas'),
 * shows a skeleton while loading, then renders CanvasViewer in read-only mode
 * with fitOnMount so the viewport frames all nodes regardless of where they sit
 * in flow-space. Calls onStats with the node count once the doc is available.
 */
export function CanvasViewerPreview({
  entityId,
  onStats,
  binding: explicitBinding,
  entityKindHint = 'canvas',
  nested = false,
  nestedKey = DEFAULT_NESTED_CANVAS_PARENT_KEY,
  readOnly = true,
}: CanvasViewerPreviewProps) {
  // `nested` constructs the standard nested binding here so host features never
  // import `nestedBinding` (cross-feature guard). Falls back to the explicit
  // binding, then top-level. Memoized: `binding` is both an effect dep (the
  // nodes.observe subscription) and the CanvasBindingContext value — a fresh
  // object literal every render would tear down + re-subscribe the observe
  // effect and re-render every context consumer on each parent re-render.
  const binding = useMemo(
    () =>
      nested
        ? nestedBinding(nestedKey)
        : (explicitBinding ?? TOP_LEVEL_BINDING),
    [nested, nestedKey, explicitBinding],
  );
  const { doc, awareness } = useEntityYjs(entityId, entityKindHint);

  useEffect(() => {
    if (!doc || !onStats) return;
    const nodes = canvasRoots(doc as Y.Doc, binding).nodes;
    const report = () => onStats({ nodeCount: nodes.size });
    report();
    nodes.observe(report);
    return () => nodes.unobserve(report);
  }, [doc, onStats, binding]);

  if (!doc || !awareness) {
    return <Skeleton active paragraph={{ rows: 3 }} className={styles.skeleton} />;
  }

  return (
    <CanvasBindingContext.Provider value={binding}>
      <div className={styles.container}>
        {readOnly ? (
          <CanvasViewer
            yDoc={doc as Y.Doc}
            awareness={awareness}
            entityId={entityId}
            fitOnMount
            hideControls
          />
        ) : (
          // Interactive mount: CanvasEditor re-resolves the SAME doc via
          // useEntityYjs(entityId, entityKindHint) and reads the nested binding
          // from CanvasBindingContext, so all its writes target `canvas.*` in the
          // host doc. The host gates this on edit permission. `embedded` — this
          // preview is always inside a host surface (e.g. a document surface)
          // that owns the page chrome, so suppress the editor's own Actionbar.
          <CanvasEditor entityId={entityId} entityKind={entityKindHint} embedded />
        )}
      </div>
    </CanvasBindingContext.Provider>
  );
}
